import dayjs from 'dayjs';
import _ from 'lodash';
import { ChevronRightIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { StockIndexPriceResponse } from '../../../response/responseSchema';
import firebaseStore from '@client/stores/firebaseStore';
import modalStore from '@client/stores/modalStore';
import { apiClient } from '@app/apiClient';
import localStorageUtil from '@client/clientUtils/localStorageUtil';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import { useCommonNavigate } from '@client/component/CommonLink';

class ViewStore {
    data: StockIndexPriceResponse[] = [];
    briefDate: dayjs.Dayjs | null = null;
    /**
     * 새로운 브리프 데이터가 있는지 여부
     */
    isNewBrief = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        const marketServiceDate = await apiClient.stock.public.getMarketServiceDate.$query();

        runInAction(() => {
            this.briefDate = dayjs(marketServiceDate || '');

            // 마지막 읽은 브리프의 날짜를 가져옴
            const lastReadBriefDate = localStorageUtil.getItem({
                key: 'lastReadBriefDate',
            });

            if (lastReadBriefDate) {
                if (this.briefDate.isAfter(dayjs(lastReadBriefDate), 'date')) {
                    this.isNewBrief = true;
                }
            } else {
                this.isNewBrief = true;
            }
        });
    }

    markAsReadBrief() {
        if (this.briefDate) {
            localStorageUtil.setItem({
                key: 'lastReadBriefDate',
                value: this.briefDate.format('YYYY-MM-DD'),
            });
            this.isNewBrief = false;
        }
    }
}

const HomePage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigate } = useCommonNavigate();

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    return (
        <div>
            {viewStore.briefDate ? (
                <button
                    className={twMerge(
                        'btn btn-soft btn-lg relative flex h-17 w-full flex-row items-center justify-between rounded font-medium',
                        viewStore.isNewBrief ? 'btn-primary' : 'text-base-content/60',
                    )}
                    onClick={async (e) => {
                        e.preventDefault();

                        const isLoggedIn = await firebaseStore.isLoggedIn();

                        if (isLoggedIn) {
                            if (viewStore.isNewBrief && viewStore.briefDate) {
                                viewStore.markAsReadBrief();
                            }

                            navigate('/daily-brief');
                        } else {
                            const { returnValue } = await modalStore.login();

                            if (returnValue) {
                                if (viewStore.isNewBrief && viewStore.briefDate) {
                                    viewStore.markAsReadBrief();
                                }

                                navigate('/daily-brief');
                            }
                        }
                    }}
                >
                    <div className="flex w-full flex-row items-center gap-1">
                        <div className="flex flex-col text-left">
                            <div className="relative flex items-center">
                                데일리 브리핑
                                {viewStore.isNewBrief && (
                                    <span className="relative -top-1.5 ml-1 flex size-1.5 opacity-70">
                                        <span className="bg-primary absolute inline-flex size-full animate-ping rounded-full opacity-75"></span>
                                        <span className="bg-primary relative inline-flex size-full rounded-full"></span>
                                    </span>
                                )}
                            </div>
                            <div className="text-xs">
                                {viewStore.briefDate?.format('M월 D일(ddd)')} 마감 기준
                            </div>
                        </div>
                    </div>
                    <div>
                        <ChevronRightIcon className="size-5" />
                    </div>
                </button>
            ) : (
                <div className="skeleton h-17 w-full rounded" />
            )}
            <div className="text-base-content/70 mt-2 w-full text-right text-xs">
                매일 오전 8시 업데이트
            </div>
        </div>
    );
});

export default HomePage;
